// BBM trilingual translations: English, French, Arabic
export const translations = {
  en: {
    dir: "ltr",
    nav: {
      services: "Services",
      industries: "Industries",
      work: "Work",
      solutions: "Solutions",
      process: "Process",
      about: "About",
      global: "Global",
      contact: "Contact",
      cta: "Request Demo",
    },
    hero: {
      eyebrow: "US-Built · Trusted Globally",
      title_pre: "Custom CRM, AI Automation &",
      title_em: "End-to-End",
      title_post: "Business Management",
      sub: "We design and engineer bespoke CRM systems, finance integrations, and full-stack digital marketing automation — from 0 to 100 — for SMEs and Fortune-grade enterprises across the US, China, and Algeria.",
      cta_primary: "Book a Free Strategy Call",
      cta_secondary: "See Our Work",
      trust: "Trusted by enterprises in the US, China & North Africa",
    },
    stats: {
      clients: "Clients served",
      projects: "Projects delivered",
      countries: "Countries",
      hours: "Hours automated",
    },
    services: {
      eyebrow: "Services",
      title: "Everything your business needs to scale — under one roof.",
      sub: "From custom-built CRM to advanced AI automation. We don't sell templates, we engineer systems.",
      items: [
        { title: "Custom CRM Systems", desc: "Bespoke CRM platforms designed from scratch for your exact workflow — not adapted from generic tools.", icon: "Database" },
        { title: "AI Automation", desc: "Intelligent agents and workflows that replace repetitive work and surface revenue opportunities.", icon: "Bot" },
        { title: "Digital Marketing 0→100", desc: "Full-funnel marketing engines: SEO, paid, content, lifecycle, attribution — built and operated end-to-end.", icon: "TrendingUp" },
        { title: "Finance Integrations", desc: "Connect billing, accounting, banking & ERP. Real-time cashflow, automated invoicing, clean books.", icon: "Wallet" },
        { title: "System Integration", desc: "Unify your stack: ERPs, e-commerce, support, telephony — into one resilient, observable platform.", icon: "Network" },
        { title: "Advanced Technologies", desc: "Cloud-native architecture, data warehousing, ML pipelines, secure APIs. Production-grade engineering.", icon: "Cpu" },
      ],
    },
    industries: {
      eyebrow: "Industries",
      title: "Built for every business — engineered for yours.",
      items: ["Retail & E-commerce", "Real Estate", "Healthcare", "Logistics", "Manufacturing", "Hospitality", "Finance & Banking", "Education", "Professional Services", "Energy & Utilities"],
    },
    process: {
      eyebrow: "How we work",
      title: "A proven process. From discovery to scale.",
      steps: [
        { n: "01", title: "Discovery", desc: "We map your operations, gaps, and revenue leaks — then design a tailored plan." },
        { n: "02", title: "Architecture", desc: "Senior engineers blueprint your CRM, integrations, and automation systems." },
        { n: "03", title: "Build", desc: "Agile sprints with weekly demos. You see progress every Friday." },
        { n: "04", title: "Launch & Scale", desc: "We deploy, train your team, and stay on as a growth partner — 0 to 100." },
      ],
    },
    portfolio: {
      eyebrow: "Selected work",
      title: "Real systems. Anonymized clients.",
      sub: "NDAs protect our partners. Here's a peek at what we've delivered.",
      items: [
        { tag: "Real Estate · USA", title: "Multi-branch CRM & lead routing", metric: "+38% close rate", color: "#0047FF" },
        { tag: "Retail · China", title: "Omnichannel inventory + POS automation", metric: "−62% stock-outs", color: "#0B132B" },
        { tag: "Logistics · MEA", title: "Fleet ops & dispatcher AI", metric: "+24% utilization", color: "#FFB800" },
        { tag: "Healthcare · USA", title: "Patient pipeline & billing integration", metric: "12 hrs saved/day", color: "#0047FF" },
        { tag: "Finance · Algeria", title: "SME accounting + invoicing portal", metric: "100% bookkeeping automated", color: "#0B132B" },
        { tag: "Hospitality · USA", title: "Reservations CRM + revenue mgmt", metric: "+19% ADR", color: "#FFB800" },
      ],
      view: "View case study",
    },
    solutions: {
      eyebrow: "Flagship solutions",
      title: "Real systems we've built, deployed and run.",
      sub: "Not theory — proprietary platforms operating today across the US, China, Europe, the Gulf and Algeria.",
      items: [
        {
          icon: "Ship",
          tag: "Cross-border · USA · China · Europe · Algeria",
          title: "Full Vehicle Import System for Algeria",
          desc: "An end-to-end platform connecting Algerian dealers to manufacturers and ports worldwide. Dealers order online; the system routes to our global partners who handle factory-to-port-Algeria logistics, with automated tracking integrated with major shipping carriers, customs transitaires and dégroupeurs.",
          features: [
            "Dealer self-service ordering portal",
            "Auto-tracking with shipping carriers",
            "Factory → port-Algeria orchestration",
            "Transitaires & dégroupeurs integration",
          ],
          partner: { label: "Strategic partner", name: "360CarExport — Largest car importer from China to Algeria" },
        },
        {
          icon: "Home",
          tag: "Home services · USA",
          title: "Home Services CRM + AI Voice Assistant",
          desc: "Full operations stack for home-services companies: AI virtual voice assistant that books jobs directly into the calendar, technician mobile app, client management, communications, phone, finance, follow-up, and marketing — one connected system.",
          features: [
            "AI voice agent — books real jobs",
            "Tech login mobile app",
            "Phone, comms, finance, follow-up",
            "Built-in marketing engine",
          ],
        },
        {
          icon: "PhoneCall",
          tag: "Call center · 24/7 · 4 languages",
          title: "AI Call Center with Live Multilingual Agents",
          desc: "Round-the-clock answering with AI agents backed by human operators speaking Arabic, French, English and Spanish. Inbound, outbound, qualification, booking, support — all logged into the CRM in real time.",
          features: [
            "24/7 — never miss a call",
            "AR · FR · EN · ES live agents",
            "AI + human hybrid model",
            "Direct CRM logging",
          ],
        },
        {
          icon: "Wrench",
          tag: "Auto parts · USA",
          title: "Parts Management System",
          desc: "A production-grade parts management platform built and operated in the US — catalog, inventory, suppliers, orders, fitments, returns — engineered for high-volume parts operations.",
          features: [
            "Catalog & fitments engine",
            "Multi-supplier inventory sync",
            "Orders & returns workflow",
            "Reporting & margins",
          ],
        },
        {
          icon: "Globe",
          tag: "Web · 1000+ sites",
          title: "Websites with Advanced Booking Systems",
          desc: "Over 1,000 websites delivered for clients — each with an advanced booking engine, payment integration, lead capture, and CRM sync. From local services to multi-location operators.",
          features: [
            "1,000+ websites delivered",
            "Advanced online booking",
            "Payment + CRM integrations",
            "Mobile-first, SEO-ready",
          ],
        },
        {
          icon: "ShoppingBag",
          tag: "E-commerce · Algeria",
          title: "Perfume Dropshipping Platform (Algeria)",
          desc: "A full e-commerce stack built in Algeria for perfume dropshipping — integrated with one of the largest perfume suppliers in the country. Catalog, orders, fulfillment, payments and delivery flows fully connected.",
          features: [
            "Direct supplier integration",
            "Dropshipping fulfillment",
            "Local payments & delivery",
            "Marketing automation built-in",
          ],
          partner: { label: "Supplier partner", name: "Largest perfume supplier in Algeria" },
        },
      ],
      cta: "Discuss your project",
    },
    about: {
      eyebrow: "About BBM",
      title: "Built in the United States by an Algerian engineer.",
      sub: "A rare blend of Algerian roots, US elite engineering, and a decade of building category-leading companies — that's the DNA behind BBM.",
      founder_name: "Liam Amine Segmane",
      founder_role: "Founder & CEO",
      founder_lead: "From software engineer in Algeria to PhD at Drexel University — Liam has spent the past decade founding, operating, and scaling 10 companies across the United States and abroad.",
      credentials_label: "Credentials",
      credentials: [
        "PhD — Drexel University, United States",
        "Software Engineer (Algeria origin)",
        "Founder of 10 companies in the United States",
        "#1 in the US since 2022 — Lead generation, System integration & Fully-AI call centers",
        "Top 10 US operators in his field",
      ],
      ventures_label: "Ventures & ownership",
      ventures: [
        { name: "Private school", loc: "United States" },
        { name: "Maintenance company", loc: "United States" },
        { name: "Lead generation company", loc: "United States" },
        { name: "Parts management business", loc: "United States" },
        { name: "Real estate company", loc: "Dubai" },
        { name: "International portfolio company", loc: "Global" },
      ],
      team_label: "The team",
      team_text: "Liam leads a hand-picked team of world-class developers, AI engineers and operators across three continents — the same caliber that built and ran his US ventures.",
      quote: "I built BBM because every company deserves the same elite engineering my US ventures had access to — at a fair price, with senior people who actually care.",
    },
    global: {
      eyebrow: "Global presence",
      title: "Two continents. One standard of excellence.",
      sub: "BBM was built in the United States and tested with high-end enterprises in the US and China. In 2025, we opened our Algeria branch with one mission: help the country develop and empower small, medium, and large companies to generate more revenue.",
      hq: "Headquarters",
      hq_loc: "United States",
      hq_desc: "Senior engineering, architecture & strategy team. Serving US & China enterprises since inception.",
      branch: "New Branch",
      branch_loc: "Algeria",
      branch_desc: "Local team helping Algerian SMEs and enterprises modernize with US-grade technology — affordable and bilingual.",
    },
    testimonials: {
      eyebrow: "What partners say",
      title: "Honest words from real operators.",
      items: [
        { quote: "BBM didn't sell us software — they re-engineered our business. The custom CRM paid for itself in 90 days.", role: "COO, Retail Group (USA)" },
        { quote: "Their automation team replaced 6 manual processes. We now close monthly books in 2 days instead of 14.", role: "CFO, Logistics Co. (China)" },
        { quote: "Finally a partner that builds for our reality — not a template. The Algeria team is exceptional.", role: "Founder, SME (Algeria)" },
      ],
    },
    contact: {
      eyebrow: "Get in touch",
      title: "Tell us about your business.",
      sub: "Free 30-min strategy call. No sales pitch — just clarity on what BBM can do for you.",
      name: "Full name",
      email: "Work email",
      company: "Company",
      phone: "Phone",
      country: "Country",
      service: "Service of interest",
      budget: "Estimated budget",
      message: "How can we help?",
      submit: "Send request",
      submitting: "Sending...",
      success: "Thank you. We will respond within 24 hours.",
      error: "Something went wrong. Please try again.",
      placeholders: {
        name: "Jane Doe",
        email: "you@company.com",
        company: "Acme Inc.",
        phone: "+1 555 000 0000",
        country: "United States",
        message: "Describe your project, goals, timeline...",
      },
      services_options: ["Custom CRM", "AI Automation", "Digital Marketing", "Finance Integration", "System Integration", "Not sure yet"],
      budget_options: ["< $10K", "$10K – $50K", "$50K – $150K", "$150K+", "Let's discuss"],
    },
    footer: {
      tagline: "System integration · Digital marketing · AI automation",
      rights: "All rights reserved.",
      links_company: "Company",
      links_services: "Services",
      links_contact: "Contact",
      newsletter: "Get monthly insights",
      newsletter_placeholder: "your@email.com",
      newsletter_cta: "Subscribe",
      newsletter_success: "Subscribed. Welcome.",
      offices: "Offices",
      us: "United States — HQ",
      dz: "Algeria — Branch",
    },
    admin: {
      title: "Lead Inbox",
      sub: "Submissions from the BBM website.",
      key_label: "Admin key",
      load: "Load leads",
      empty: "No leads yet.",
      status_new: "New",
      status_contacted: "Contacted",
      status_qualified: "Qualified",
      status_won: "Won",
      status_lost: "Lost",
    },
    chat: {
      title: "BBM Assistant",
      subtitle: "Ask anything · we reply in seconds",
      placeholder: "Type your message...",
      send: "Send",
      open: "Chat with us",
      close: "Close chat",
      greeting: "Hi! I'm the BBM Assistant. How can I help your business today?",
      suggestions: [
        "What does BBM do?",
        "I need a custom CRM",
        "Pricing & strategy call",
      ],
      typing: "Typing...",
      error: "Something went wrong. Please try again.",
      reset: "Start a new chat",
      poweredBy: "Powered by BBM",
    },
  },

  fr: {
    dir: "ltr",
    nav: {
      services: "Services",
      industries: "Secteurs",
      work: "Réalisations",
      solutions: "Solutions",
      process: "Méthode",
      about: "À propos",
      global: "International",
      contact: "Contact",
      cta: "Demander une démo",
    },
    hero: {
      eyebrow: "Conçu aux USA · Reconnu mondialement",
      title_pre: "CRM sur-mesure, automatisation IA &",
      title_em: "Gestion d'entreprise",
      title_post: "de bout en bout",
      sub: "Nous concevons des systèmes CRM personnalisés, des intégrations financières et une automatisation marketing complète — de 0 à 100 — pour PME et grandes entreprises aux États-Unis, en Chine et en Algérie.",
      cta_primary: "Réserver un appel stratégique",
      cta_secondary: "Voir nos réalisations",
      trust: "La confiance d'entreprises aux États-Unis, en Chine et en Afrique du Nord",
    },
    stats: {
      clients: "Clients servis",
      projects: "Projets livrés",
      countries: "Pays",
      hours: "Heures automatisées",
    },
    services: {
      eyebrow: "Services",
      title: "Tout ce dont votre entreprise a besoin pour grandir — sous un même toit.",
      sub: "Du CRM sur-mesure à l'automatisation IA avancée. Pas de templates, nous concevons des systèmes.",
      items: [
        { title: "Systèmes CRM sur-mesure", desc: "Plateformes CRM conçues de zéro pour votre flux de travail exact — jamais adaptées d'un outil générique.", icon: "Database" },
        { title: "Automatisation IA", desc: "Agents intelligents et workflows qui remplacent le travail répétitif et révèlent des opportunités de revenus.", icon: "Bot" },
        { title: "Marketing Digital 0→100", desc: "Moteurs marketing complets : SEO, paid, contenu, attribution — construits et opérés de A à Z.", icon: "TrendingUp" },
        { title: "Intégrations financières", desc: "Connectez facturation, comptabilité, banque et ERP. Trésorerie en temps réel, facturation automatisée.", icon: "Wallet" },
        { title: "Intégration de systèmes", desc: "Unifiez votre stack : ERP, e-commerce, support, téléphonie — une plateforme résiliente.", icon: "Network" },
        { title: "Technologies avancées", desc: "Architecture cloud-native, data warehouse, pipelines ML, APIs sécurisées. Ingénierie de production.", icon: "Cpu" },
      ],
    },
    industries: {
      eyebrow: "Secteurs",
      title: "Conçu pour tout business — adapté au vôtre.",
      items: ["Retail & E-commerce", "Immobilier", "Santé", "Logistique", "Industrie", "Hôtellerie", "Finance & Banque", "Éducation", "Services professionnels", "Énergie"],
    },
    process: {
      eyebrow: "Notre méthode",
      title: "Un processus éprouvé. De la découverte à l'échelle.",
      steps: [
        { n: "01", title: "Découverte", desc: "Nous cartographions vos opérations et fuites de revenus — puis concevons un plan." },
        { n: "02", title: "Architecture", desc: "Nos ingénieurs seniors conçoivent votre CRM, intégrations et automatisations." },
        { n: "03", title: "Construction", desc: "Sprints agiles avec démos hebdomadaires. Progression visible chaque vendredi." },
        { n: "04", title: "Lancement & Scale", desc: "Déploiement, formation et accompagnement long terme — de 0 à 100." },
      ],
    },
    portfolio: {
      eyebrow: "Réalisations",
      title: "Vrais systèmes. Clients anonymisés.",
      sub: "Les NDA protègent nos partenaires. Aperçu de nos livraisons.",
      items: [
        { tag: "Immobilier · USA", title: "CRM multi-agences & routage de leads", metric: "+38% taux de closing", color: "#0047FF" },
        { tag: "Retail · Chine", title: "Inventaire omnicanal + automatisation POS", metric: "−62% ruptures", color: "#0B132B" },
        { tag: "Logistique · MEA", title: "Ops flotte & IA dispatcher", metric: "+24% utilisation", color: "#FFB800" },
        { tag: "Santé · USA", title: "Pipeline patient & intégration facturation", metric: "12 h économisées/jour", color: "#0047FF" },
        { tag: "Finance · Algérie", title: "Comptabilité PME + portail facturation", metric: "100% comptabilité automatisée", color: "#0B132B" },
        { tag: "Hôtellerie · USA", title: "CRM réservations + revenue mgmt", metric: "+19% ADR", color: "#FFB800" },
      ],
      view: "Voir l'étude de cas",
    },
    solutions: {
      eyebrow: "Solutions phares",
      title: "Des systèmes réels, construits, déployés et opérés.",
      sub: "Pas de la théorie — des plateformes propriétaires en production aujourd'hui aux USA, en Chine, en Europe, dans le Golfe et en Algérie.",
      items: [
        {
          icon: "Ship",
          tag: "International · USA · Chine · Europe · Algérie",
          title: "Système complet d'importation de véhicules pour l'Algérie",
          desc: "Une plateforme de bout en bout connectant les concessionnaires algériens aux constructeurs et ports du monde entier. Le concessionnaire commande en ligne ; le système route vers nos partenaires globaux qui gèrent l'usine → port d'Algérie, avec un tracking automatisé intégré aux principaux transporteurs maritimes, transitaires et dégroupeurs.",
          features: [
            "Portail de commande dédié aux concessionnaires",
            "Tracking automatisé avec les transporteurs",
            "Orchestration usine → port d'Algérie",
            "Intégration transitaires & dégroupeurs",
          ],
          partner: { label: "Partenaire stratégique", name: "360CarExport — Plus grand importateur de voitures de Chine vers l'Algérie" },
        },
        {
          icon: "Home",
          tag: "Services à domicile · USA",
          title: "CRM Home Services + Assistant Vocal IA",
          desc: "Stack opérationnelle complète pour les entreprises de services à domicile : assistant vocal IA qui réserve des rendez-vous directement dans l'agenda, app technicien, gestion client, communications, téléphonie, finance, suivi et marketing — un système unifié.",
          features: [
            "Assistant vocal IA — prend de vrais rdv",
            "App mobile pour techniciens",
            "Téléphonie, finance, suivi intégrés",
            "Moteur marketing intégré",
          ],
        },
        {
          icon: "PhoneCall",
          tag: "Centre d'appels · 24/7 · 4 langues",
          title: "Centre d'appels IA avec agents humains multilingues",
          desc: "Réception d'appels 24h/24, 7j/7 avec agents IA et opérateurs humains parlant arabe, français, anglais et espagnol. Inbound, outbound, qualification, prise de rdv, support — tout enregistré dans le CRM en temps réel.",
          features: [
            "24/7 — aucun appel manqué",
            "Agents live AR · FR · EN · ES",
            "Modèle hybride IA + humain",
            "Logging CRM direct",
          ],
        },
        {
          icon: "Wrench",
          tag: "Pièces auto · USA",
          title: "Système de gestion de pièces détachées",
          desc: "Plateforme de gestion de pièces de niveau production, construite et opérée aux USA — catalogue, stock, fournisseurs, commandes, compatibilités, retours — pour des opérations à fort volume.",
          features: [
            "Catalogue & moteur de compatibilités",
            "Synchro stock multi-fournisseurs",
            "Workflow commandes & retours",
            "Reporting & marges",
          ],
        },
        {
          icon: "Globe",
          tag: "Web · 1000+ sites",
          title: "Sites web avec système de réservation avancé",
          desc: "Plus de 1 000 sites livrés pour nos clients — chacun avec un moteur de réservation avancé, intégration de paiement, capture de leads et synchro CRM. Du local au multi-sites.",
          features: [
            "Plus de 1 000 sites livrés",
            "Réservation en ligne avancée",
            "Intégrations paiement + CRM",
            "Mobile-first, prêt SEO",
          ],
        },
        {
          icon: "ShoppingBag",
          tag: "E-commerce · Algérie",
          title: "Plateforme e-commerce parfums (Algérie)",
          desc: "Stack e-commerce complète construite en Algérie pour le dropshipping de parfums — intégrée à l'un des plus grands fournisseurs de parfums du pays. Catalogue, commandes, fulfillment, paiements et livraisons entièrement connectés.",
          features: [
            "Intégration fournisseur directe",
            "Dropshipping clé en main",
            "Paiements & livraison locale",
            "Automatisation marketing intégrée",
          ],
          partner: { label: "Fournisseur partenaire", name: "Plus grand fournisseur de parfums d'Algérie" },
        },
      ],
      cta: "Parlons de votre projet",
    },
    about: {
      eyebrow: "À propos de BBM",
      title: "Construit aux États-Unis par un ingénieur algérien.",
      sub: "Un mélange rare de racines algériennes, d'ingénierie d'élite américaine et d'une décennie à bâtir des entreprises leaders — voilà l'ADN de BBM.",
      founder_name: "Liam Amine Segmane",
      founder_role: "Fondateur & CEO",
      founder_lead: "D'ingénieur logiciel en Algérie à doctorat à l'université Drexel — Liam a passé la dernière décennie à fonder, opérer et faire croître 10 entreprises aux États-Unis et à l'international.",
      credentials_label: "Parcours",
      credentials: [
        "Doctorat (PhD) — Drexel University, États-Unis",
        "Ingénieur logiciel (origine Algérie)",
        "Fondateur de 10 entreprises aux États-Unis",
        "N°1 aux USA depuis 2022 — Génération de leads, intégration de systèmes & centres d'appels 100% IA",
        "Top 10 des opérateurs américains dans son secteur",
      ],
      ventures_label: "Entreprises & participations",
      ventures: [
        { name: "École privée", loc: "États-Unis" },
        { name: "Société de maintenance", loc: "États-Unis" },
        { name: "Société de génération de leads", loc: "États-Unis" },
        { name: "Gestion de pièces détachées", loc: "États-Unis" },
        { name: "Société immobilière", loc: "Dubaï" },
        { name: "Holding international", loc: "International" },
      ],
      team_label: "L'équipe",
      team_text: "Liam dirige une équipe triée sur le volet — développeurs, ingénieurs IA et opérateurs de classe mondiale répartis sur trois continents — du même calibre que celles qui ont bâti ses entreprises américaines.",
      quote: "J'ai créé BBM parce que chaque entreprise mérite l'ingénierie d'élite dont mes sociétés américaines ont profité — à un prix juste, avec des seniors qui s'investissent vraiment.",
    },
    global: {
      eyebrow: "Présence internationale",
      title: "Deux continents. Une seule exigence d'excellence.",
      sub: "BBM est né aux États-Unis et a été éprouvé auprès d'entreprises de premier rang aux USA et en Chine. En 2025, nous avons ouvert notre branche en Algérie avec une mission : aider le pays à se développer et permettre aux entreprises de générer plus de revenus.",
      hq: "Siège",
      hq_loc: "États-Unis",
      hq_desc: "Équipe d'ingénieurs seniors, architecture & stratégie. Au service des entreprises US et chinoises depuis nos débuts.",
      branch: "Nouvelle branche",
      branch_loc: "Algérie",
      branch_desc: "Une équipe locale aidant les PME et grandes entreprises algériennes à se moderniser avec une technologie de niveau US — abordable et bilingue.",
    },
    testimonials: {
      eyebrow: "Ils en parlent",
      title: "Témoignages d'opérationnels.",
      items: [
        { quote: "BBM ne nous a pas vendu un logiciel — ils ont refondu notre business. Le CRM s'est rentabilisé en 90 jours.", role: "COO, Groupe Retail (USA)" },
        { quote: "Leur équipe a remplacé 6 processus manuels. Clôture mensuelle en 2 jours au lieu de 14.", role: "CFO, Logistique (Chine)" },
        { quote: "Enfin un partenaire qui conçoit pour notre réalité — pas un template. L'équipe Algérie est exceptionnelle.", role: "Fondateur, PME (Algérie)" },
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Parlez-nous de votre business.",
      sub: "Appel stratégique gratuit de 30 min. Pas de discours commercial — juste de la clarté.",
      name: "Nom complet",
      email: "Email professionnel",
      company: "Entreprise",
      phone: "Téléphone",
      country: "Pays",
      service: "Service d'intérêt",
      budget: "Budget estimé",
      message: "Comment pouvons-nous aider ?",
      submit: "Envoyer",
      submitting: "Envoi...",
      success: "Merci. Nous répondons sous 24 heures.",
      error: "Une erreur s'est produite. Réessayez.",
      placeholders: {
        name: "Jean Dupont",
        email: "vous@entreprise.com",
        company: "Acme SARL",
        phone: "+213 555 000 000",
        country: "Algérie",
        message: "Décrivez votre projet, objectifs, délais...",
      },
      services_options: ["CRM sur-mesure", "Automatisation IA", "Marketing digital", "Intégration financière", "Intégration système", "Pas sûr"],
      budget_options: ["< 10K $", "10K – 50K $", "50K – 150K $", "150K+ $", "À discuter"],
    },
    footer: {
      tagline: "Intégration système · Marketing digital · Automatisation IA",
      rights: "Tous droits réservés.",
      links_company: "Entreprise",
      links_services: "Services",
      links_contact: "Contact",
      newsletter: "Recevez nos analyses mensuelles",
      newsletter_placeholder: "vous@email.com",
      newsletter_cta: "S'abonner",
      newsletter_success: "Inscription confirmée.",
      offices: "Bureaux",
      us: "États-Unis — Siège",
      dz: "Algérie — Branche",
    },
    admin: {
      title: "Boîte de leads",
      sub: "Soumissions depuis le site BBM.",
      key_label: "Clé admin",
      load: "Charger",
      empty: "Aucun lead pour l'instant.",
      status_new: "Nouveau",
      status_contacted: "Contacté",
      status_qualified: "Qualifié",
      status_won: "Gagné",
      status_lost: "Perdu",
    },
    chat: {
      title: "Assistant BBM",
      subtitle: "Posez votre question · réponse en quelques secondes",
      placeholder: "Tapez votre message...",
      send: "Envoyer",
      open: "Discuter avec nous",
      close: "Fermer le chat",
      greeting: "Bonjour ! Je suis l'assistant BBM. Comment puis-je aider votre entreprise aujourd'hui ?",
      suggestions: [
        "Que fait BBM ?",
        "Je veux un CRM sur-mesure",
        "Tarifs & appel stratégique",
      ],
      typing: "En train d'écrire...",
      error: "Une erreur s'est produite. Réessayez.",
      reset: "Nouvelle conversation",
      poweredBy: "Propulsé par BBM",
    },
  },

  ar: {
    dir: "rtl",
    nav: {
      services: "الخدمات",
      industries: "القطاعات",
      work: "أعمالنا",
      solutions: "حلولنا",
      process: "منهجيتنا",
      about: "من نحن",
      global: "حضور عالمي",
      contact: "تواصل",
      cta: "اطلب عرضًا",
    },
    hero: {
      eyebrow: "صُنع في الولايات المتحدة · موثوق عالميًا",
      title_pre: "أنظمة CRM مخصصة وأتمتة بالذكاء الاصطناعي",
      title_em: "وإدارة أعمال",
      title_post: "من الألف إلى الياء",
      sub: "نصمم ونبني أنظمة CRM مخصصة، تكاملات مالية، وأتمتة تسويق رقمي شاملة — من الصفر إلى المئة — للشركات الصغيرة والمتوسطة والكبرى في الولايات المتحدة والصين والجزائر.",
      cta_primary: "احجز مكالمة استراتيجية مجانية",
      cta_secondary: "اطلع على أعمالنا",
      trust: "تثق بنا شركات في الولايات المتحدة والصين وشمال إفريقيا",
    },
    stats: {
      clients: "عميل",
      projects: "مشروع",
      countries: "دولة",
      hours: "ساعة مؤتمتة",
    },
    services: {
      eyebrow: "الخدمات",
      title: "كل ما يحتاجه عملك للنمو — تحت سقف واحد.",
      sub: "من CRM مخصص إلى أتمتة متقدمة بالذكاء الاصطناعي. لا قوالب — بل أنظمة هندسية حقيقية.",
      items: [
        { title: "أنظمة CRM مخصصة", desc: "منصات CRM مصممة من الصفر لتناسب سير عملك بدقة — لا اقتباس من أدوات عامة.", icon: "Database" },
        { title: "أتمتة الذكاء الاصطناعي", desc: "وكلاء أذكياء وسير عمل يستبدل المهام المتكررة ويكشف فرص الإيرادات.", icon: "Bot" },
        { title: "التسويق الرقمي من 0 إلى 100", desc: "محركات تسويق متكاملة: SEO، إعلانات مدفوعة، محتوى، إسناد — تُبنى وتُشغّل بالكامل.", icon: "TrendingUp" },
        { title: "تكاملات مالية", desc: "ربط الفوترة والمحاسبة والبنوك وERP. تدفقات نقدية لحظية وفوترة مؤتمتة.", icon: "Wallet" },
        { title: "تكامل الأنظمة", desc: "وحّد منظومتك: ERP، تجارة إلكترونية، دعم، اتصالات — في منصة موحدة وقوية.", icon: "Network" },
        { title: "تقنيات متقدمة", desc: "بنية سحابية حديثة، مستودعات بيانات، خطوط ML، واجهات API آمنة. هندسة جاهزة للإنتاج.", icon: "Cpu" },
      ],
    },
    industries: {
      eyebrow: "القطاعات",
      title: "مصمم لكل عمل — مهندس لعملك.",
      items: ["التجزئة والتجارة الإلكترونية", "العقارات", "الصحة", "اللوجستيات", "الصناعة", "الضيافة", "المالية والبنوك", "التعليم", "الخدمات المهنية", "الطاقة"],
    },
    process: {
      eyebrow: "كيف نعمل",
      title: "منهجية مُجرّبة. من الاكتشاف إلى التوسع.",
      steps: [
        { n: "01", title: "الاكتشاف", desc: "نرسم خريطة عملياتك وفجواتك وفرص النمو — ثم نصمم خطة مخصصة." },
        { n: "02", title: "التصميم", desc: "مهندسون كبار يصممون نظام CRM والتكاملات والأتمتة الخاصة بك." },
        { n: "03", title: "البناء", desc: "سباقات تطوير رشيقة مع عروض أسبوعية. ترى التقدم كل جمعة." },
        { n: "04", title: "الإطلاق والتوسع", desc: "ننشر، ندرّب فريقك، ونرافقك كشريك نمو — من 0 إلى 100." },
      ],
    },
    portfolio: {
      eyebrow: "أعمال مختارة",
      title: "أنظمة حقيقية. عملاء محميون.",
      sub: "اتفاقيات السرية تحمي شركاءنا. إليكم لمحة عما أنجزناه.",
      items: [
        { tag: "عقارات · الولايات المتحدة", title: "CRM متعدد الفروع وتوجيه العملاء", metric: "+38% معدل إغلاق", color: "#0047FF" },
        { tag: "تجزئة · الصين", title: "مخزون متعدد القنوات وأتمتة نقاط البيع", metric: "−62% نفاد المخزون", color: "#0B132B" },
        { tag: "لوجستيات · MEA", title: "إدارة أسطول وذكاء اصطناعي للتوزيع", metric: "+24% استخدام", color: "#FFB800" },
        { tag: "صحة · الولايات المتحدة", title: "مسار المرضى وتكامل الفوترة", metric: "12 ساعة موفّرة يوميًا", color: "#0047FF" },
        { tag: "مالية · الجزائر", title: "محاسبة ومنصة فوترة للشركات", metric: "100% أتمتة محاسبية", color: "#0B132B" },
        { tag: "ضيافة · الولايات المتحدة", title: "CRM الحجوزات وإدارة الإيرادات", metric: "+19% ADR", color: "#FFB800" },
      ],
      view: "عرض الدراسة",
    },
    solutions: {
      eyebrow: "حلولنا الرائدة",
      title: "أنظمة حقيقية بنيناها ونشغّلها ونديرها اليوم.",
      sub: "ليست نظرية — منصات مملوكة تعمل الآن في الولايات المتحدة والصين وأوروبا والخليج والجزائر.",
      items: [
        {
          icon: "Ship",
          tag: "عابر للحدود · أمريكا · الصين · أوروبا · الجزائر",
          title: "نظام متكامل لاستيراد السيارات إلى الجزائر",
          desc: "منصة شاملة تربط الوكلاء الجزائريين بالمصانع والموانئ حول العالم. يقدّم الوكيل طلبه إلكترونيًا، فيوجّهه النظام إلى شركائنا الدوليين الذين يتكفلون بكل شيء من المصنع إلى الميناء الجزائري، مع تتبّع آلي مع كبرى شركات الشحن وشركاء التخليص والتجميع في الجزائر.",
          features: [
            "بوابة طلبيات ذاتية للوكلاء",
            "تتبّع آلي مع شركات الشحن",
            "تنسيق من المصنع إلى ميناء الجزائر",
            "تكامل مع المخلّصين والمجمّعين (Dégroupeurs)",
          ],
          partner: { label: "شريك استراتيجي", name: "360CarExport — أكبر مستورد سيارات من الصين إلى الجزائر" },
        },
        {
          icon: "Home",
          tag: "خدمات منزلية · الولايات المتحدة",
          title: "CRM لخدمات المنازل + مساعد صوتي بالذكاء الاصطناعي",
          desc: "حزمة تشغيل متكاملة لشركات خدمات المنازل: مساعد صوتي بالذكاء الاصطناعي يحجز المواعيد مباشرة في التقويم، تطبيق للفنيين، إدارة عملاء، اتصالات، هاتف، مالية، متابعة، وتسويق — نظام واحد متصل.",
          features: [
            "وكيل صوتي AI — يحجز مواعيد حقيقية",
            "تطبيق دخول للفنيين",
            "هاتف، اتصالات، مالية، متابعة",
            "محرّك تسويق مدمج",
          ],
        },
        {
          icon: "PhoneCall",
          tag: "مركز اتصال · 24/7 · 4 لغات",
          title: "مركز اتصال بالذكاء الاصطناعي مع وكلاء بشريين متعددي اللغات",
          desc: "الردّ على المكالمات على مدار الساعة بوكلاء AI مدعومين بمشغّلين بشريين يتحدثون العربية والفرنسية والإنجليزية والإسبانية. مكالمات واردة وصادرة، تأهيل، حجز، دعم — كل شيء يُسجَّل في الـ CRM فورًا.",
          features: [
            "24/7 — لا تفقد أي مكالمة",
            "وكلاء أحياء AR · FR · EN · ES",
            "نموذج هجين AI + بشر",
            "تسجيل مباشر في CRM",
          ],
        },
        {
          icon: "Wrench",
          tag: "قطع غيار · الولايات المتحدة",
          title: "نظام إدارة قطع الغيار",
          desc: "منصة إدارة قطع غيار جاهزة للإنتاج، مبنية ومُشغَّلة في الولايات المتحدة — كتالوج، مخزون، موردون، طلبات، توافق، مرتجعات — مصممة لعمليات بحجم كبير.",
          features: [
            "محرك كتالوج وتوافقات",
            "مزامنة مخزون متعدد الموردين",
            "سير عمل الطلبات والمرتجعات",
            "تقارير وهوامش ربح",
          ],
        },
        {
          icon: "Globe",
          tag: "ويب · أكثر من 1000 موقع",
          title: "مواقع إلكترونية مع نظام حجز متقدم",
          desc: "سلّمنا أكثر من 1000 موقع لعملائنا — كل موقع بمحرك حجز متقدم، تكامل دفع، التقاط عملاء محتملين ومزامنة CRM. من خدمات محلية إلى مشغّلين متعددي الفروع.",
          features: [
            "أكثر من 1000 موقع مُسلَّم",
            "حجز إلكتروني متقدم",
            "تكاملات دفع و CRM",
            "Mobile-first ومُهيّأ لـ SEO",
          ],
        },
        {
          icon: "ShoppingBag",
          tag: "تجارة إلكترونية · الجزائر",
          title: "منصة عطور بالـ Dropshipping (الجزائر)",
          desc: "حزمة تجارة إلكترونية كاملة بُنيت في الجزائر للدروبشيبينغ للعطور — متكاملة مع أكبر مورد عطور في البلاد. كتالوج، طلبات، تجهيز، مدفوعات وشحن — كلها مترابطة.",
          features: [
            "تكامل مباشر مع المورد",
            "Dropshipping جاهز",
            "مدفوعات وتوصيل محلي",
            "أتمتة تسويق مدمجة",
          ],
          partner: { label: "شريك توريد", name: "أكبر مورد عطور في الجزائر" },
        },
      ],
      cta: "نناقش مشروعك",
    },
    about: {
      eyebrow: "من نحن",
      title: "صُنع في الولايات المتحدة على يد مهندس جزائري.",
      sub: "مزيج نادر من جذور جزائرية، وهندسة أمريكية من الطراز الأول، وعقد كامل من بناء شركات رائدة — هذا هو الحمض النووي لـ BBM.",
      founder_name: "ليام أمين سغمان",
      founder_role: "المؤسس والرئيس التنفيذي",
      founder_lead: "من مهندس برمجيات في الجزائر إلى دكتوراه من جامعة Drexel في الولايات المتحدة — أمضى ليام العقد الماضي في تأسيس وإدارة وتنمية 10 شركات في أمريكا وخارجها.",
      credentials_label: "السيرة",
      credentials: [
        "دكتوراه — جامعة Drexel، الولايات المتحدة",
        "مهندس برمجيات (أصول جزائرية)",
        "مؤسس 10 شركات في الولايات المتحدة",
        "الأول في أمريكا منذ 2022 — توليد العملاء وتكامل الأنظمة ومراكز الاتصال بالذكاء الاصطناعي بالكامل",
        "ضمن أفضل 10 مشغّلين أمريكيين في مجاله",
      ],
      ventures_label: "الشركات والممتلكات",
      ventures: [
        { name: "مدرسة خاصة", loc: "الولايات المتحدة" },
        { name: "شركة صيانة", loc: "الولايات المتحدة" },
        { name: "شركة توليد عملاء محتملين", loc: "الولايات المتحدة" },
        { name: "إدارة قطع الغيار", loc: "الولايات المتحدة" },
        { name: "شركة عقارية", loc: "دبي" },
        { name: "شركة استثمارية دولية", loc: "عالمي" },
      ],
      team_label: "الفريق",
      team_text: "يقود ليام فريقًا منتقى بعناية من أمهر المطورين ومهندسي الذكاء الاصطناعي والمشغّلين في العالم، موزعين على ثلاث قارات — بنفس مستوى الفرق التي بنت شركاته الأمريكية وأدارتها.",
      quote: "أسّست BBM لأن كل شركة تستحق نفس الهندسة الراقية التي حظيت بها مشاريعي الأمريكية — بسعر عادل، ومع مهنيين كبار يهتمون فعلًا.",
    },
    global: {
      eyebrow: "حضور عالمي",
      title: "قارتان. معيار واحد للتميّز.",
      sub: "وُلدت BBM في الولايات المتحدة وأُثبتت مع كبرى الشركات في أمريكا والصين. في 2025، افتتحنا فرعنا في الجزائر بهدف واحد: مساعدة البلد على التطور وتمكين الشركات من زيادة إيراداتها.",
      hq: "المقر الرئيسي",
      hq_loc: "الولايات المتحدة",
      hq_desc: "فريق هندسي وتصميمي واستراتيجي. نخدم مؤسسات أمريكية وصينية منذ التأسيس.",
      branch: "فرع جديد",
      branch_loc: "الجزائر",
      branch_desc: "فريق محلي يساعد الشركات الجزائرية على التحديث بتقنية بمستوى أمريكي — بأسعار في المتناول وثنائي اللغة.",
    },
    testimonials: {
      eyebrow: "ماذا يقول شركاؤنا",
      title: "كلمات صادقة من مشغّلين حقيقيين.",
      items: [
        { quote: "لم تبعنا BBM برنامجًا — بل أعادت هندسة عملنا. CRM المخصص استعاد كلفته في 90 يومًا.", role: "COO، مجموعة تجزئة (الولايات المتحدة)" },
        { quote: "فريق الأتمتة استبدل 6 عمليات يدوية. نقفل الحسابات في يومين بدل 14.", role: "CFO، شركة لوجستيات (الصين)" },
        { quote: "أخيرًا شريك يبني وفق واقعنا — لا قوالب. فريق الجزائر استثنائي.", role: "مؤسس، PME (الجزائر)" },
      ],
    },
    contact: {
      eyebrow: "تواصل معنا",
      title: "أخبرنا عن عملك.",
      sub: "مكالمة استراتيجية مجانية 30 دقيقة. لا عرض مبيعات — فقط وضوح.",
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      company: "الشركة",
      phone: "الهاتف",
      country: "الدولة",
      service: "الخدمة المطلوبة",
      budget: "الميزانية التقديرية",
      message: "كيف نستطيع مساعدتك؟",
      submit: "إرسال",
      submitting: "جاري الإرسال...",
      success: "شكرًا. سنرد خلال 24 ساعة.",
      error: "حدث خطأ. يرجى المحاولة لاحقًا.",
      placeholders: {
        name: "الاسم الكامل",
        email: "you@company.com",
        company: "اسم الشركة",
        phone: "+213 555 000 000",
        country: "الجزائر",
        message: "صف المشروع والأهداف والمواعيد...",
      },
      services_options: ["CRM مخصص", "أتمتة AI", "تسويق رقمي", "تكامل مالي", "تكامل أنظمة", "غير متأكد"],
      budget_options: ["< 10K $", "10K – 50K $", "50K – 150K $", "150K+ $", "نناقش"],
    },
    footer: {
      tagline: "تكامل الأنظمة · التسويق الرقمي · أتمتة الذكاء الاصطناعي",
      rights: "جميع الحقوق محفوظة.",
      links_company: "الشركة",
      links_services: "الخدمات",
      links_contact: "تواصل",
      newsletter: "احصل على رؤى شهرية",
      newsletter_placeholder: "your@email.com",
      newsletter_cta: "اشترك",
      newsletter_success: "تم الاشتراك.",
      offices: "المكاتب",
      us: "الولايات المتحدة — المقر",
      dz: "الجزائر — الفرع",
    },
    admin: {
      title: "صندوق العملاء المحتملين",
      sub: "العملاء المحتملون من موقع BBM.",
      key_label: "مفتاح الإدارة",
      load: "تحميل",
      empty: "لا يوجد عملاء بعد.",
      status_new: "جديد",
      status_contacted: "تم الاتصال",
      status_qualified: "مؤهل",
      status_won: "فاز",
      status_lost: "خسر",
    },
    chat: {
      title: "مساعد BBM",
      subtitle: "اسأل ما تريد · نرد في ثوانٍ",
      placeholder: "اكتب رسالتك...",
      send: "إرسال",
      open: "تحدّث معنا",
      close: "إغلاق المحادثة",
      greeting: "مرحبًا! أنا مساعد BBM. كيف يمكنني مساعدة عملك اليوم؟",
      suggestions: [
        "ما الذي تقدّمه BBM؟",
        "أحتاج إلى CRM مخصص",
        "الأسعار ومكالمة استراتيجية",
      ],
      typing: "يكتب...",
      error: "حدث خطأ. يرجى المحاولة لاحقًا.",
      reset: "محادثة جديدة",
      poweredBy: "بدعم من BBM",
    },
  },
};

export const LANGS = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "ar", label: "AR" },
];
